// Integrations Settings — PSD §10.1, §10.2 (F10.1, F10.2). Settings screen.

import { useRef, useState } from 'react';
import { useStore } from '../../store/selectors';
import { parseImport as parseCarta } from '../../integrations/carta';
import { parseImport as parsePulley } from '../../integrations/pulley';
import { parseImport as parseLedgy } from '../../integrations/ledgy';
import { parseMarketData } from '../../integrations/marketData';

export function IntegrationsSection() {
  const settings = useStore((s) => s.settings);
  const setDefaults = useStore((s) => s.setDefaults);
  const commitSnapshot = useStore((s) => s.commitSnapshot);
  const pushToast = useStore((s) => s.pushToast);
  const fileRef = useRef<HTMLInputElement>(null);
  const marketRef = useRef<HTMLInputElement>(null);
  const [provider, setProvider] = useState<'carta' | 'pulley' | 'ledgy'>('carta');

  const handleCapTableImport = async (file: File) => {
    try {
      const parser = provider === 'carta' ? parseCarta : provider === 'pulley' ? parsePulley : parseLedgy;
      const { capTable, warnings } = await parser(file, settings.activeEntityName ?? 'Imported Entity');
      commitSnapshot(capTable.entityName, capTable);
      pushToast('success', `Imported ${capTable.instruments.length} instruments from ${provider}.`);
      if (warnings.length > 0) pushToast('info', warnings.join('; '));
    } catch (e) {
      pushToast('error', `Import failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleMarketDataImport = async (file: File) => {
    try {
      const bundle = await parseMarketData(file);
      const patch: Record<string, unknown> = {};
      if (bundle.volatility !== null) patch.defaultVolatility = bundle.volatility;
      if (bundle.riskFreeRate !== null) patch.defaultRiskFreeRate = bundle.riskFreeRate;
      setDefaults(patch);
      pushToast(
        'success',
        `Imported market data: vol=${bundle.volatility ?? '—'}, rfr=${bundle.riskFreeRate ?? '—'}, ${bundle.multiples.length} multiples`,
      );
    } catch (e) {
      pushToast('error', `Market data import failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <div className="settings-section">
      <h3>Integrations</h3>

      <div className="form-group">
        <label htmlFor="provider">Cap-Table Provider</label>
        <select id="provider" value={provider} onChange={(e) => setProvider(e.target.value as typeof provider)}>
          <option value="carta">Carta</option>
          <option value="pulley">Pulley</option>
          <option value="ledgy">Ledgy</option>
        </select>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept=".csv,.json"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleCapTableImport(f);
          e.target.value = '';
        }}
      />
      <button className="btn btn-secondary" onClick={() => fileRef.current?.click()}>
        Import Cap Table ({provider})
      </button>

      <div className="form-group mt-4">
        <label>Market Data</label>
        <p className="field-hint">Import volatility, risk-free rate, and multiples from a JSON file.</p>
      </div>

      <input
        ref={marketRef}
        type="file"
        accept=".csv,.json"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleMarketDataImport(f);
          e.target.value = '';
        }}
      />
      <button className="btn btn-secondary" onClick={() => marketRef.current?.click()}>
        Import Market Data
      </button>
    </div>
  );
}
